import React, { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { RelatedEventCard } from './RelatedEventCard';
import styles from './RelatedEvents.module.scss';
import { Divider } from '../_shared/Divider';
import { useRelatedAndPromotedEventsLazy, useRelatedEventsPicoXTracking } from './hooks';
import { LoadingSkeleton } from '@eventbrite/legacy-infra.loading-skeleton';
import { PriceFloor, RelatedEvent, RelatedEventsData } from './types';
import { BasicInfo } from '@/types';
import { mergeRelatedAndPromotedEvents, createTrackingInformationGetter } from './utils';
import { assignRefsToNode } from '@/utils/utils';
import { AffiliateCodes, DOM_IDS } from '../_shared/constants';
import { EventInteraction, useTracking } from '@/tracking';
import { useAppContext } from '../_shared/context/AppContext';
import { isEventEnded, isEventLive } from '../Body/utils/bodyUtils';
import { LISTING_HEAP_IDS } from '@/components/_shared/constants';
import { useComponentVisibilityWithTracking } from '@/hooks/useComponentVisibility';
import { useStage } from '@/hooks/useStage';
import {
  useIsLinearScoreEnabled,
  useIsRelatedEventsDenseAdsEnabled,
  useIsRelatedHigherPriceEnabled,
} from '@/bsp/config';
import { useTicketInformation } from '@/hooks/useTicketInformation/useTicketInformation';

export const DEFAULT_MAX_EVENTS = 4;
// Treatment of `enable_increase_impressions_on_related_events`. With dense ads
// the merge places promoted events at positions [1, 2, 4], yielding
// [ad, ad, organic, ad, organic] when capped at 5. Capping at 4 would chop the
// last organic slot and ship 3 ads + 1 organic, which is not the experiment
// design.
export const DENSE_MAX_EVENTS = 5;

export interface SessionData {
  userId: string | null;
  sessionId: string | null;
  guestId: string | null;
}

export interface RelatedEventsProps {
  eventId: string;
  isBodyRendered?: boolean;
  eventBasicInfo: BasicInfo;
  sessionData?: SessionData;
}

const getEventsToShow = (relatedEvents: RelatedEventsData | null, isDenseAdsEnabled: boolean): RelatedEvent[] => {
  if (!relatedEvents?.events?.length) {
    return [];
  }
  const maxEvents = isDenseAdsEnabled ? DENSE_MAX_EVENTS : DEFAULT_MAX_EVENTS;
  return relatedEvents.events.slice(0, maxEvents);
};

const buildRelatedEventList = (
  eventsToShow: RelatedEvent[],
  affiliateCode: AffiliateCodes | undefined,
  onCardClick: ((event: RelatedEvent) => void) | undefined,
) => {
  return eventsToShow.map((event, index) => {
    const isLastItem = index === eventsToShow.length - 1;
    const eventKey = event.id || `event-${index}`;

    return (
      <React.Fragment key={eventKey}>
        <RelatedEventCard relatedEvent={event} affiliateCode={affiliateCode} onCardClick={onCardClick} />
        {!isLastItem && <Divider />}
      </React.Fragment>
    );
  });
};

export const RelatedEvents: React.FC<RelatedEventsProps> = ({
  eventId,
  isBodyRendered,
  eventBasicInfo,
  sessionData,
}) => {
  const { formatMessage } = useIntl();
  const {
    data: { locale },
  } = useAppContext();
  const stage = useStage();

  const isDenseAdsEnabled = useIsRelatedEventsDenseAdsEnabled(eventBasicInfo.organizer?.id);

  // Higher-price experiment: when enabled, gate related events to the current
  // event's max ticket price (same currency) so cheaper events are zeroed out.
  const { isEnabled: isHigherPriceEnabled, isLoading: isHigherPriceLoading } = useIsRelatedHigherPriceEnabled();

  // Linear-ranker paid-boost experiment: when enabled, ask the search service to
  // rank related events with the linear ranker via `score_mode: 'linear'`.
  const { isEnabled: isLinearScoreEnabled, isLoading: isLinearScoreLoading } = useIsLinearScoreEnabled();

  // Only fetch on-Eventbrite ticket info when needed: in the treatment arm, and only when
  // the sync external-ticketing price (on basicInfo) isn't already available.
  const externalMaxPrice = eventBasicInfo.externalTicketing?.maximumTicketPrice;
  const hasExternalMaxPrice = typeof externalMaxPrice?.value === 'number' && !!externalMaxPrice.currency;
  const [ticketInformation, isTicketInformationLoading] = useTicketInformation({
    enabled: isHigherPriceEnabled && !hasExternalMaxPrice,
  });
  const priceFloor = useMemo<PriceFloor | null>(() => {
    if (!isHigherPriceEnabled) {
      return null;
    }
    // Pick the source we enabled, not `??`: a disabled useTicketInformation still serves a
    // sibling-warmed React Query cache, which must not override the external price.
    const maxPrice = hasExternalMaxPrice ? externalMaxPrice : ticketInformation?.maximumTicketPrice;
    if (typeof maxPrice?.value !== 'number' || !maxPrice.currency) {
      return null;
    }
    return { value: maxPrice.value, currency: maxPrice.currency };
  }, [isHigherPriceEnabled, hasExternalMaxPrice, ticketInformation, externalMaxPrice]);

  // Wait for the experiment inputs before fetching: the Orion gate (so treatment isn't
  // read as control mid-settle), and in treatment the ticket info supplying the floor.
  // Cheap since the carousel is lazy-loaded. Also hold until the linear-score gate has
  // settled, so a treatment visitor isn't read as control and sent the default ranker.
  const isFetchReady =
    !isHigherPriceLoading && !isLinearScoreLoading && (!isHigherPriceEnabled || !isTicketInformationLoading);

  const { relatedEvents, promotedEvents, isLoading, elementRef } = useRelatedAndPromotedEventsLazy(
    eventBasicInfo,
    stage,
    eventId,
    isBodyRendered,
    isDenseAdsEnabled,
    priceFloor,
    isFetchReady,
    isLinearScoreEnabled ? 'linear' : undefined,
  );

  const relatedAndPromotedEvents = useMemo(
    () => mergeRelatedAndPromotedEvents(relatedEvents, promotedEvents, isDenseAdsEnabled),
    [relatedEvents, promotedEvents, isDenseAdsEnabled],
  );

  const eventsToShow = useMemo(
    () => getEventsToShow(relatedAndPromotedEvents, isDenseAdsEnabled),
    [relatedAndPromotedEvents, isDenseAdsEnabled],
  );

  // PicoX dispatches for slate render (`related_eb_events_recommended`) and
  // card click (`eb_event_page_opened`). Encapsulated in a hook so the
  // component body stays under complexity / many-returns thresholds.
  // `isLoading` is forwarded so the slate impression waits for both the
  // organic and promoted fetches to settle (avoids two dispatches per slate).
  // Mirrors the eb-ui counterpart (PR eventbrite/eb-ui#36290).
  //
  // `surfaceEventId` is the page event itself (-> PicoX `eb_event_id`); on
  // a series-child listing this is the child id, matching what
  // `log_engagement` already sends as `retrieval_context.related_event_id`
  // via `createTrackingInformationGetter` below.
  //
  // `parentEventId` is the series/timed-entry parent of `surfaceEventId`
  // when applicable (-> PicoX `parent_eb_event_id`). Mirrors the
  // dataLayer fold (`app/src/utils/dataLayer.ts:158-159`) so PicoX
  // dashboards keyed on `parent_eb_event_id` group series-child slates
  // under the canonical series id. For non-series surfaces this collapses
  // back to `eventBasicInfo.id`, preserving the previous wire shape.
  const surfaceEventId = eventBasicInfo.id;
  const parentEventId =
    eventBasicInfo.isSeries && eventBasicInfo.seriesId ? eventBasicInfo.seriesId : eventBasicInfo.id;

  const { trackEventPageOpened } = useRelatedEventsPicoXTracking({
    eventsToShow,
    surfaceEventId,
    parentEventId,
    isLoading,
  });

  const getTrackingInformation = useMemo(() => {
    if (!sessionData) {
      return (eventInteraction: EventInteraction) => ({
        ...eventInteraction,
        user: { locale },
        place: { name: 'loading', position: 0, page: 1, context: {} },
      });
    }

    return createTrackingInformationGetter(
      {
        id: eventBasicInfo.id,
        isOnline: eventBasicInfo.isOnline,
        isEnded: isEventEnded(eventBasicInfo.status),
      },
      relatedAndPromotedEvents?.events || [],
      locale,
      sessionData,
    );
  }, [eventBasicInfo, relatedAndPromotedEvents?.events, locale, sessionData]);

  const { ref: trackingRef } = useTracking<HTMLDivElement>(getTrackingInformation);

  const visibilityTrackingRef = useComponentVisibilityWithTracking(undefined, {
    heap: {
      eventName: LISTING_HEAP_IDS.RECOMMENDATIONS_VIEW,
      eventBucketLabel: 'Other Events you may like',
    },
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const hasEvents = eventsToShow.length > 0;

  useEffect(() => {
    const node = containerRef.current;
    if (node) {
      assignRefsToNode([elementRef, trackingRef, hasEvents ? visibilityTrackingRef : undefined], node);
    }
  }, [elementRef, trackingRef, hasEvents, visibilityTrackingRef]);

  const affiliateCode = useMemo(() => {
    if (isEventEnded(eventBasicInfo.status)) {
      return AffiliateCodes.AFF_RELATED_EVENTS_EXPIRED;
    }
    if (isEventLive(eventBasicInfo.status)) {
      return AffiliateCodes.AFF_RELATED_EVENTS_LIVE;
    }
  }, [eventBasicInfo]);

  const relatedAndPromotedEventsList = useMemo(
    () => buildRelatedEventList(eventsToShow, affiliateCode, trackEventPageOpened),
    [eventsToShow, affiliateCode, trackEventPageOpened],
  );

  const title = useMemo(() => formatMessage({ id: 'You might also like...' }), [formatMessage]);

  const subtitle = useMemo(
    () =>
      formatMessage({
        id: 'Browse more events with different dates, prices, and formats to find your next great experience.',
      }),
    [formatMessage],
  );

  const shouldShowSkeleton = isLoading;
  const shouldShowEmpty = !isLoading && !hasEvents;
  const shouldShowEvents = !isLoading && hasEvents;

  const containerStyle = useMemo(
    () => ({
      ...(shouldShowEmpty && { minHeight: '1px' }),
    }),
    [shouldShowEmpty],
  );

  const containerProps = shouldShowEvents ? { 'data-testid': 'related-events' } : {};

  return (
    <div ref={containerRef} style={containerStyle} {...containerProps} id={DOM_IDS.RELATED_EVENTS}>
      <div
        className={`${styles.copyWrapper} ${!shouldShowEvents ? styles.hiddenSeoCopy : ''}`}
        data-testid="related-events-copy"
      >
        <h2 className={styles.title}>{title}</h2>
        <h3 className={styles.subtitle}>{subtitle}</h3>
      </div>
      {shouldShowSkeleton ? <LoadingSkeleton height={550} /> : null}
      {shouldShowEvents ? (
        <section className={styles.relatedEventsContainer}>
          {relatedAndPromotedEventsList}
          <div className={styles.dividerContainer}>
            <Divider />
          </div>
        </section>
      ) : null}
    </div>
  );
};
